import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, AlertTriangle, XCircle, Boxes, History } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, Panel, TypeBadge, TH } from "@/components/common";
import { api, fmtQty, fmtDate } from "@/lib/api";

function Kpi({ label, value, icon: Icon, tone, testid, sub, delay }) {
  const tones = { slate: "text-slate-900", amber: "text-orange-600", rose: "text-rose-600", emerald: "text-emerald-600" };
  return (
    <div className="rounded-md border bg-white p-5 shadow-sm sk-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500">{label}<Icon className={`h-5 w-5 ${tones[tone]}`} /></div>
      <div className={`mt-3 font-mono text-4xl font-bold tracking-tight ${tones[tone]}`} data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function AlertTable({ rows, testid, showManque }) {
  if (!rows.length) return <p className="p-5 text-sm text-slate-500" data-testid={`${testid}-empty`}>Aucun article.</p>;
  return (
    <div className="max-h-96 overflow-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow><TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead><TableHead className={`${TH} text-right`}>Stock</TableHead><TableHead className={`${TH} text-right`}>Seuil</TableHead>{showManque && <TableHead className={`${TH} text-right`}>Manque</TableHead>}</TableRow></TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.code} data-testid={`${testid}-row-${a.code}`}>
              <TableCell className="font-mono font-semibold">{a.code}</TableCell>
              <TableCell>{a.designation}</TableCell>
              <TableCell className="text-right font-mono">{fmtQty(a.stock)} <span className="text-xs text-slate-400">{a.unite}</span></TableCell>
              <TableCell className="text-right font-mono text-slate-500">{fmtQty(a.seuil_min)}</TableCell>
              {showManque && <TableCell className="text-right font-mono font-semibold text-orange-600">{fmtQty(a.manque)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data));
    load();
    window.addEventListener("sk-stock-changed", load);
    return () => window.removeEventListener("sk-stock-changed", load);
  }, []);
  if (!d) return <div className="text-slate-500" data-testid="dashboard-loading">Chargement…</div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble des quantités, alertes et capacités de kits." testid="dashboard-title" />
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Articles référencés" value={d.total_articles} icon={PackageSearch} tone="slate" testid="kpi-total-articles" delay={0} />
        <Kpi label="Articles sous seuil" value={d.sous_seuil_count} icon={AlertTriangle} tone="amber" testid="kpi-sous-seuil" delay={60} />
        <Kpi label="Articles à zéro" value={d.zero_count} icon={XCircle} tone="rose" testid="kpi-zero" delay={120} />
        <Kpi label="Kits réalisables" value={`${d.kits_realisables} / ${d.total_kits}`} icon={Boxes} tone="emerald" testid="kpi-kits" sub="kits avec au moins 1 unité réalisable" delay={180} />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <Panel title="Alertes de stock" icon={AlertTriangle} testid="alerts-panel" className="xl:col-span-3">
          <Tabs defaultValue="seuil" className="p-0">
            <TabsList className="m-4 mb-0 h-11">
              <TabsTrigger value="seuil" className="h-9" data-testid="alerts-tab-seuil">Sous seuil ({d.sous_seuil_count})</TabsTrigger>
              <TabsTrigger value="zero" className="h-9" data-testid="alerts-tab-zero">À zéro ({d.zero_count})</TabsTrigger>
            </TabsList>
            <TabsContent value="seuil"><AlertTable rows={d.sous_seuil} testid="sous-seuil-table" showManque /></TabsContent>
            <TabsContent value="zero"><AlertTable rows={d.zero} testid="zero-table" /></TabsContent>
          </Tabs>
        </Panel>
        <Panel title="Kits réalisables" icon={Boxes} testid="kits-capacity-panel" className="xl:col-span-2" actions={<Link to="/kits" className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="dashboard-kits-link">Voir les kits</Link>}>
          <div className="overflow-x-auto">
            <Table data-testid="kits-capacity-table">
              <TableHeader><TableRow><TableHead className={TH}>Kit</TableHead><TableHead className={`${TH} text-right`}>Réalisables</TableHead><TableHead className={TH}>Composant limitant</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.kits.map((k) => {
                  const lim = k.components.find((c) => c.code === k.limiting);
                  return (
                    <TableRow key={k.code} data-testid={`kits-capacity-row-${k.code}`}>
                      <TableCell><div className="font-mono font-semibold">{k.code}</div><div className="text-xs text-slate-500">{k.designation}</div></TableCell>
                      <TableCell className={`text-right font-mono text-lg font-bold ${k.buildable === 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid={`kit-buildable-${k.code}`}>{k.buildable}</TableCell>
                      <TableCell className="text-sm"><span className="font-mono font-semibold">{k.limiting}</span> <span className="text-slate-500">{lim?.designation}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Panel>
        <Panel title="Derniers mouvements" icon={History} testid="recent-movements-panel" className="xl:col-span-5" actions={<Link to="/mouvements?tab=historique" className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="dashboard-history-link">Historique complet</Link>}>
          <div className="overflow-x-auto">
            <Table data-testid="recent-movements-table">
              <TableHeader><TableRow><TableHead className={TH}>Date</TableHead><TableHead className={TH}>Type</TableHead><TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead><TableHead className={`${TH} text-right`}>Quantité</TableHead><TableHead className={TH}>Utilisateur</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.recent_movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(m.date)}</TableCell>
                    <TableCell><TypeBadge type={m.type} /></TableCell>
                    <TableCell className="font-mono font-semibold">{m.code}</TableCell>
                    <TableCell>{m.designation}</TableCell>
                    <TableCell className="text-right font-mono">{fmtQty(m.quantity)}</TableCell>
                    <TableCell className="text-sm">{m.user_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
